<template>
  <section class="space-y-4">
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">Customer Mappings</h1>
        <p class="mt-1 text-sm text-gray-500">
          Map the last letter of a PO number's two-letter suffix to a customer and a calendar color.
        </p>
      </div>
      <button class="btn-primary" type="button" @click="openCreateDialog">Add mapping</button>
    </header>

    <v-alert v-if="deleteError" type="error" :text="deleteError" closable @click:close="deleteError = null" />

    <v-alert v-if="store.error" type="error" :text="store.error" />
    <v-progress-linear v-else-if="store.loading" indeterminate color="primary" />

    <div v-else class="rounded-xl border border-gray-200 bg-white p-4">
      <p v-if="store.items.length === 0" class="m-0 text-sm text-gray-500">
        No customer mappings yet — add one to start coloring POs on the capacity calendar.
      </p>
      <table v-else class="w-full border-collapse text-sm">
        <thead>
          <tr class="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th class="py-2 pr-3">Color</th>
            <th class="py-2 pr-3">Code</th>
            <th class="py-2 pr-3">Customer</th>
            <th class="py-2 pr-3">Updated</th>
            <th class="py-2 pr-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="mapping in store.items" :key="mapping.id" class="border-b border-gray-100">
            <td class="py-2 pr-3">
              <span
                class="inline-block h-5 w-5 rounded border border-gray-300 align-middle"
                :style="{ backgroundColor: mapping.color }"
                :title="mapping.color"
              />
            </td>
            <td class="py-2 pr-3 font-mono font-semibold text-gray-900">{{ mapping.code }}</td>
            <td class="py-2 pr-3 text-gray-900">{{ mapping.customerName }}</td>
            <td class="py-2 pr-3 text-gray-500">{{ formatDate(mapping.updatedAt) }}</td>
            <td class="py-2 pr-3 text-right">
              <button
                type="button"
                class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700"
                :aria-label="`Edit mapping ${mapping.code}`"
                @click="openEditDialog(mapping)"
              >
                <v-icon icon="mdi-pencil-outline" size="16" />
              </button>
              <button
                type="button"
                class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700"
                :aria-label="`Delete mapping ${mapping.code}`"
                @click="pendingDelete = mapping"
              >
                <v-icon icon="mdi-trash-can-outline" size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Teleport to="body">
      <div
        v-if="dialogOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        @click.self="closeDialog"
        @keydown.escape="closeDialog"
      >
        <form class="card w-full max-w-sm" @submit.prevent="submitDialog">
          <h3 class="m-0 text-base font-semibold text-gray-900">{{ editing ? 'Edit mapping' : 'Add mapping' }}</h3>

          <label class="mt-4 block text-xs font-medium text-gray-600" for="mapping-code">Code (single letter)</label>
          <input
            id="mapping-code"
            v-model="form.code"
            maxlength="1"
            pattern="[A-Za-z]"
            required
            class="mt-1 w-16 rounded-md border border-gray-300 px-2 py-1.5 text-center font-mono text-sm uppercase"
          />

          <label class="mt-3 block text-xs font-medium text-gray-600" for="mapping-name">Customer name</label>
          <input
            id="mapping-name"
            v-model="form.customerName"
            required
            class="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />

          <label class="mt-3 block text-xs font-medium text-gray-600" for="mapping-color">Color</label>
          <div class="mt-1 flex items-center gap-2">
            <input id="mapping-color" v-model="form.color" type="color" class="h-8 w-12 cursor-pointer rounded border border-gray-300" />
            <span class="font-mono text-xs text-gray-500">{{ form.color.toUpperCase() }}</span>
          </div>
          <div class="mt-2 flex gap-1.5" role="group" aria-label="Preset colors">
            <button
              v-for="preset in PRESET_COLORS"
              :key="preset"
              type="button"
              class="h-6 w-6 rounded border"
              :class="form.color.toUpperCase() === preset ? 'border-gray-900 ring-2 ring-blue-300' : 'border-gray-300'"
              :style="{ backgroundColor: preset }"
              :aria-label="`Use color ${preset}`"
              @click="form.color = preset"
            />
          </div>

          <p v-if="dialogError" class="mt-3 text-sm text-red-700" role="alert">{{ dialogError }}</p>

          <div class="mt-5 flex justify-end gap-2">
            <button class="btn-secondary" type="button" @click="closeDialog">Cancel</button>
            <button class="btn-primary" type="submit" :disabled="saving">
              {{ saving ? 'Saving…' : editing ? 'Save changes' : 'Add mapping' }}
            </button>
          </div>
        </form>
      </div>
    </Teleport>

    <ConfirmDialog
      :open="pendingDelete !== null"
      title="Delete customer mapping?"
      :message="deleteMessage"
      confirm-label="Delete"
      @confirm="confirmDelete"
      @cancel="pendingDelete = null"
    />
  </section>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import type { CustomerMapping } from '@/types/customerMapping';
  import { extractErrorMessage } from '@/utils/apiError';
  import { customerLabel } from '@/utils/customerColors';

  const PRESET_COLORS = ['#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#10B981', '#EC4899', '#14B8A6', '#64748B'];
  const DEFAULT_COLOR = PRESET_COLORS[0];

  const store = useCustomerMappingStore();

  const dialogOpen = ref(false);
  const editing = ref<CustomerMapping | null>(null);
  const saving = ref(false);
  const dialogError = ref<string | null>(null);
  const form = reactive({ code: '', customerName: '', color: DEFAULT_COLOR });

  const pendingDelete = ref<CustomerMapping | null>(null);
  const deleteError = ref<string | null>(null);
  const deleteMessage = computed(() =>
    pendingDelete.value
      ? `POs ending in "${pendingDelete.value.code}" will lose the ${customerLabel(pendingDelete.value)} color and render as unmapped.`
      : '',
  );

  function openCreateDialog(): void {
    editing.value = null;
    form.code = '';
    form.customerName = '';
    form.color = DEFAULT_COLOR;
    dialogError.value = null;
    dialogOpen.value = true;
  }

  function openEditDialog(mapping: CustomerMapping): void {
    editing.value = mapping;
    form.code = mapping.code;
    form.customerName = mapping.customerName ?? '';
    form.color = mapping.color;
    dialogError.value = null;
    dialogOpen.value = true;
  }

  function closeDialog(): void {
    dialogOpen.value = false;
  }

  async function submitDialog(): Promise<void> {
    saving.value = true;
    dialogError.value = null;
    const input = { code: form.code.toUpperCase(), customerName: form.customerName.trim(), color: form.color };
    try {
      if (editing.value) {
        await store.update(editing.value.id, input);
      } else {
        await store.create(input);
      }
      dialogOpen.value = false;
    } catch (submitError) {
      dialogError.value = extractErrorMessage(submitError);
    } finally {
      saving.value = false;
    }
  }

  async function confirmDelete(): Promise<void> {
    deleteError.value = null;
    const mapping = pendingDelete.value;
    pendingDelete.value = null;
    if (!mapping) return;
    try {
      await store.remove(mapping.id);
    } catch (removeError) {
      deleteError.value = extractErrorMessage(removeError);
    }
  }

  function formatDate(value: string): string {
    return value.slice(0, 'YYYY-MM-DD'.length);
  }

  onMounted(() => {
    void store.load();
  });
</script>
